import React, { useState, useEffect } from 'react';
import { reportsAPI } from '../../services/api';
import Card from '../common/Card';
import StatsCard from '../dashboard/StatsCard';
import Loading from '../common/Loading';
import Button from '../common/Button';
import { formatDate, handleApiError } from '../../utils/helpers';

const ReportsPage = () => {
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      setLoading(true);
      setError(null);

      const [statsResponse, activityResponse] = await Promise.all([
        reportsAPI.getStats(),
        reportsAPI.getActivity(5),
      ]);

      setStats(statsResponse.data.stats);
      setActivity(activityResponse.data);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Loading fullScreen />;
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{error}</p>
        <Button onClick={fetchReports} variant="secondary">
          Try Again
        </Button>
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">System Reports</h1>
        <p className="text-gray-600 mt-1">Comprehensive statistics and analytics</p>
      </div>

      {/* Overall Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Total Users"
          value={stats.users.total}
          color="blue"
          icon="👥"
        />
        <StatsCard
          title="Total Agencies"
          value={stats.agencies.total}
          color="purple"
          icon="🏢"
        />
        <StatsCard
          title="Total Clients"
          value={stats.clients.total}
          color="green"
          icon="💼"
        />
        <StatsCard
          title="Total Posts"
          value={stats.posts.total}
          color="yellow"
          icon="📄"
        />
      </div>

      {/* Revenue Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatsCard
          title="Monthly Revenue"
          value={`$${stats.revenue.totalMonthlyRevenue.toFixed(2)}`}
          color="green"
          icon="💰"
        />
        <StatsCard
          title="Active Subscriptions"
          value={stats.revenue.activeSubscriptions}
          color="blue"
          icon="✅"
        />
        <StatsCard
          title="Free Subscriptions"
          value={stats.revenue.freeSubscriptions}
          color="yellow"
          icon="🆓"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Users by Role */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Users by Role</h2>
          <div className="space-y-3">
            {Object.entries(stats.users.byRole).map(([role, count]) => (
              <div key={role} className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 capitalize">
                  {role.replace('_', ' ')}
                </span>
                <span className="text-lg font-bold text-gray-900">{count}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* Agencies by Status */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Agencies by Status</h2>
          <div className="space-y-3">
            {Object.entries(stats.agencies.byStatus).map(([status, count]) => (
              <div key={status} className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 capitalize">
                  {status === 'active' ? '✅ Active' : '❌ Inactive'}
                </span>
                <span className="text-lg font-bold text-gray-900">{count}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* Agencies by Plan */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Agencies by Plan</h2>
          <div className="space-y-3">
            {stats.agencies.byPlan.length === 0 ? (
              <p className="text-sm text-gray-500">No agencies with plans</p>
            ) : (
              stats.agencies.byPlan.map((item, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">
                    {item.plan}
                  </span>
                  <span className="text-lg font-bold text-gray-900">{item.count}</span>
                </div>
              ))
            )}
          </div>
        </Card>

        {/* Posts by Status */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Posts by Status</h2>
          <div className="space-y-3">
            {Object.entries(stats.posts.byStatus).map(([status, count]) => (
              <div key={status} className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 capitalize">
                  {status.replace('_', ' ')}
                </span>
                <span className="text-lg font-bold text-gray-900">{count}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Plans Statistics */}
      <Card className="p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Plans Statistics</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <span className="text-sm font-medium text-gray-700">Total Plans</span>
            <span className="text-2xl font-bold text-gray-900">{stats.plans.total}</span>
          </div>
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <span className="text-sm font-medium text-gray-700">Active Plans</span>
            <span className="text-2xl font-bold text-green-600">{stats.plans.active}</span>
          </div>
        </div>
      </Card>

      {/* Recent Activity */}
      {activity && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Agencies */}
          <Card className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Recent Agencies</h2>
            {activity.recentAgencies.length === 0 ? (
              <p className="text-sm text-gray-500">No agencies yet</p>
            ) : (
              <div className="space-y-3">
                {activity.recentAgencies.map((agency) => (
                  <div key={agency.id} className="pb-3 border-b border-gray-200 last:border-0">
                    <p className="text-sm font-medium text-gray-900">{agency.name}</p>
                    <p className="text-xs text-gray-500 mt-1">{agency.admin_email}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {formatDate(agency.created_at)}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Recent Users */}
          <Card className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Recent Users</h2>
            {activity.recentUsers.length === 0 ? (
              <p className="text-sm text-gray-500">No users yet</p>
            ) : (
              <div className="space-y-3">
                {activity.recentUsers.map((user) => (
                  <div key={user.id} className="pb-3 border-b border-gray-200 last:border-0">
                    <p className="text-sm font-medium text-gray-900">{user.name}</p>
                    <p className="text-xs text-gray-500 mt-1">{user.email}</p>
                    <p className="text-xs text-gray-400 mt-1 capitalize">
                      {user.role} • {formatDate(user.created_at)}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Recent Posts */}
          <Card className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Recent Posts</h2>
            {activity.recentPosts.length === 0 ? (
              <p className="text-sm text-gray-500">No posts yet</p>
            ) : (
              <div className="space-y-3">
                {activity.recentPosts.map((post) => (
                  <div key={post.id} className="pb-3 border-b border-gray-200 last:border-0">
                    <p className="text-sm font-medium text-gray-900">{post.title}</p>
                    <p className="text-xs text-gray-500 mt-1">{post.company_name}</p>
                    <p className="text-xs text-gray-400 mt-1 capitalize">
                      {post.status} • {formatDate(post.created_at)}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
};

export default ReportsPage;








